// Printing barcode labels for products and variants.
//
// One entry point shared by the Product Master's "Print Barcode" button and any
// other screen that needs labels: it honours the saved Barcode Settings (label
// size, position, which content rows to show), streams the label to the USB
// label printer — TSPL or ESC/POS, see labelPrint.ts — when one is configured,
// and otherwise opens a print sheet laid out at the same physical label size.

import { getBarcodeSettings, sizeToMm, sizeToPx } from './barcode';
import { LabelTextRow } from './tspl';
import { labelPrintingAvailable, printLabelSpec } from './labelPrint';

export interface LabelRow {
  name: string;
  code: string;
  price?: number;
  mrp?: number;
  hsn?: string;
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Content rows for one label, filtered by the saved Barcode Settings.
function contentRows(row: LabelRow, money: (n: number) => string): LabelTextRow[] {
  const s = getBarcodeSettings();
  const out: LabelTextRow[] = [];
  // Price carries the configured emphasis (bold / bigger font).
  if (s.content.price && !s.withoutPrice && row.price != null)
    out.push({ label: 'Price', value: money(row.price), ...s.priceStyle });
  if (s.content.mrp && row.mrp != null) out.push({ label: 'MRP', value: money(row.mrp) });
  if (s.content.hsn && row.hsn) out.push({ label: 'HSN', value: row.hsn });
  return out;
}

// A stand-in for real bars — the same decorative rendering the Barcode Printing
// preview uses. The scannable output is the TSPL path; this sheet is a fallback
// for eyeballing and for printers driven through the browser.
function barsHtml(code: string): string {
  return code
    .split('')
    .map((ch) => `<span style="width:${(ch.charCodeAt(0) % 4) + 1}px"></span>`)
    .join('');
}

export async function printProductLabels(
  rows: LabelRow[],
  copies: number,
  storeName: string,
  money: (n: number) => string
): Promise<void> {
  const settings = getBarcodeSettings();
  const printable = rows.filter((r) => r.code);
  if (!printable.length || copies < 1) return;

  if (labelPrintingAvailable()) {
    const mm = sizeToMm(settings.labelSize);
    for (const row of printable) {
      // Sequentially, so the printer's buffer isn't flooded by a big matrix.
      // eslint-disable-next-line no-await-in-loop
      await printLabelSpec({
        widthMm: mm.w,
        heightMm: mm.h,
        position: settings.position,
        header: settings.content.shopName ? storeName : undefined,
        name: settings.content.productName ? row.name : undefined,
        rows: contentRows(row, money),
        code: row.code,
        copies,
        marginTopMm: settings.marginTopMm,
        marginLeftMm: settings.marginLeftMm,
        marginBottomMm: settings.marginBottomMm,
        marginRightMm: settings.marginRightMm,
        across: settings.labelsAcross,
        columnGapMm: settings.columnGapMm,
        gapMm: settings.labelGapMm,
      });
    }
    return;
  }

  // Browser fallback: one div per copy at the configured label size.
  const px = sizeToPx(settings.labelSize);
  const labels = printable
    .flatMap((row) =>
      Array.from({ length: copies }, () => {
        const meta = contentRows(row, money)
          .map(
            (r) =>
              `<div class="meta" style="font-weight:${r.bold ? 700 : 400};font-size:${
                9 * (r.scale ?? 1)
              }px">${esc(r.label)}: ${esc(r.value)}</div>`
          )
          .join('');
        const bars = `<div class="bars">${barsHtml(row.code)}</div><div class="code">${esc(row.code)}</div>`;
        const text = `${settings.content.shopName ? `<div class="shop">${esc(storeName)}</div>` : ''}${
          settings.content.productName ? `<div class="name">${esc(row.name)}</div>` : ''
        }${meta}`;
        return `<div class="label">${settings.position === 'Top' ? bars + text : text + bars}</div>`;
      })
    )
    .join('');

  const w = window.open('', '_blank', 'width=520,height=640');
  if (!w) return;
  w.document.write(`<!doctype html>
<html><head><meta charset="utf-8" /><title>Barcode Labels</title>
<style>
  body { font-family: Arial, sans-serif; margin: 8px; display: flex; flex-wrap: wrap; gap: 6px; }
  .label {
    width: ${px.w}px; height: ${px.h}px; border: 1px dashed #999;
    padding: ${Math.round(settings.marginTopMm * 3.4)}px ${Math.round(settings.marginRightMm * 3.4)}px ${Math.round(settings.marginBottomMm * 3.4)}px ${Math.round(settings.marginLeftMm * 3.4)}px;
    box-sizing: border-box; display: flex; flex-direction: column; align-items: center;
    justify-content: center; overflow: hidden; page-break-inside: avoid;
  }
  .shop { font-size: 9px; font-weight: 700; }
  .name { font-size: 10px; font-weight: 700; text-align: center; }
  .meta { font-size: 9px; }
  .bars { display: flex; align-items: flex-end; height: ${Math.round(px.h * 0.35)}px; gap: 1px; }
  .bars span { background: #000; height: 100%; display: inline-block; }
  .code { font-size: 9px; letter-spacing: 1px; }
  @media print { body { margin: 0; } .label { border: none; } }
</style></head>
<body onload="window.print();">${labels}</body></html>`);
  w.document.close();
}
